"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { CheckCircle2, Loader2 } from "lucide-react";
import Botcheck from "@/components/forms/Botcheck";
import MarketingConsent from "@/components/forms/MarketingConsent";
import { t } from "@/content/i18n/messages";
import { defaultLocale, getLocalizedPathname, type Locale } from "@/lib/i18n";
import { isBotSubmission } from "@/lib/botcheck";
import { trackEvent } from "@/lib/analytics";
import { hasJoinedHubWaitlist, rememberHubWaitlist, submitHubWaitlist } from "@/lib/agk-lead";
import { COMPANY_SIZES } from "@/content/ai-governance/kit";

export const HUB_INTERESTS = [
  "All 48 templates",
  "AI risk register",
  "Control owners and attestation",
  "Board report and KRIs",
  "90-day planner",
  "Policy assistant",
  "MCP connection",
  "Update alerts",
  "Deploy in our own cloud account (enterprise)",
  "Offline deployment with a private AI model (enterprise)",
] as const;

const inputClass =
  "mt-2 w-full rounded-lg border border-border bg-background px-4 py-3 text-base text-neutral-emphasis focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary";

/** Waitlist sign-up for the AI Governance Hub membership (no payment taken). */
export default function HubWaitlistForm({
  locale = defaultLocale,
  placement,
  id = "hub-waitlist",
}: {
  locale?: Locale;
  placement: string;
  id?: string;
}) {
  const tr = (key: string) => t(locale, key);
  const [status, setStatus] = useState<"idle" | "sending" | "done" | "error">("idle");
  const [already, setAlready] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const sending = useRef(false);

  useEffect(() => {
    if (hasJoinedHubWaitlist()) setAlready(true);
  }, []);

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (sending.current) return;
    if (isBotSubmission(event.currentTarget)) {
      setStatus("error");
      return;
    }
    const f = new FormData(event.currentTarget);
    const get = (k: string) => String(f.get(k) ?? "");
    sending.current = true;
    setStatus("sending");
    try {
      await submitHubWaitlist(
        {
          name: get("name"),
          email: get("email"),
          company: get("company"),
          companySize: get("company_size"),
          interest: get("interest"),
          marketingConsent,
          placement,
          sourcePath: window.location.pathname,
          locale,
        },
        process.env.NEXT_PUBLIC_WEB3FORMS_KEY,
      );
    } catch {
      setStatus("error");
      sending.current = false;
      return;
    }
    try {
      trackEvent("agk_hub_waitlist", { category: "lead", placement, interest: get("interest"), locale });
    } catch {
      // ignore
    }
    rememberHubWaitlist();
    setStatus("done");
    sending.current = false;
  }

  if (status === "done" || already) {
    return (
      <div role="status" className="rounded-xl border border-success/30 bg-success/5 p-5">
        <CheckCircle2 className="h-7 w-7 text-success" aria-hidden />
        <p className="mt-2 font-semibold text-neutral-emphasis">{tr("You're on the AI Governance Hub waitlist.")}</p>
        <p className="mt-1 text-sm text-neutral-muted">{tr("We'll email you before the AI Governance Hub opens. No payment is taken now.")}</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} aria-label={tr("Join the AI Governance Hub waitlist")}>
      <Botcheck />
      <fieldset disabled={status === "sending"} className="space-y-4 disabled:opacity-70">
        <legend className="sr-only">{tr("Join the AI Governance Hub waitlist")}</legend>
        <div className="grid gap-4 sm:grid-cols-2">
          <label htmlFor={`${id}-name`} className="block text-sm font-medium text-neutral-emphasis">
            {tr("Name")}
            <input id={`${id}-name`} name="name" autoComplete="name" required maxLength={120} className={inputClass} />
          </label>
          <label htmlFor={`${id}-email`} className="block text-sm font-medium text-neutral-emphasis">
            {tr("Work email")}
            <input id={`${id}-email`} name="email" type="email" autoComplete="email" required maxLength={254} className={inputClass} />
          </label>
          <label htmlFor={`${id}-company`} className="block text-sm font-medium text-neutral-emphasis">
            {tr("Company")}
            <input id={`${id}-company`} name="company" autoComplete="organization" required maxLength={160} className={inputClass} />
          </label>
          <label htmlFor={`${id}-size`} className="block text-sm font-medium text-neutral-emphasis">
            {tr("Company size")}
            <select id={`${id}-size`} name="company_size" defaultValue="" className={inputClass}>
              <option value="">{tr("Select")}</option>
              {COMPANY_SIZES.map((s) => (
                <option key={s} value={s}>
                  {s} {tr("staff")}
                </option>
              ))}
            </select>
          </label>
          <label htmlFor={`${id}-interest`} className="block text-sm font-medium text-neutral-emphasis sm:col-span-2">
            {tr("What would you use most?")}
            <select
              id={`${id}-interest`}
              name="interest"
              defaultValue=""
              className={inputClass}
              onChange={(e) => {
                // Interest before (or without) sign-up. Values are fixed English codes.
                if (e.target.value) trackEvent("agk_hub_interest_select", { category: "engagement", interest: e.target.value, placement, location: "membership" });
              }}
            >
              <option value="">{tr("Select")}</option>
              {HUB_INTERESTS.map((i) => (
                <option key={i} value={i}>
                  {tr(i)}
                </option>
              ))}
            </select>
          </label>
        </div>
        <MarketingConsent
          id={`${id}-marketing`}
          checked={marketingConsent}
          onChange={setMarketingConsent}
          disabled={status === "sending"}
          locale={locale}
        />
        {status === "error" && (
          <p role="alert" className="text-sm text-destructive">
            {tr("We could not process your request. Please try again or")}{" "}
            <Link href={getLocalizedPathname("/contact/", locale)} className="underline">
              {tr("contact us")}
            </Link>
            .
          </p>
        )}
        <button
          type="submit"
          className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-lg bg-primary px-5 py-3 font-semibold text-primary-foreground hover:bg-primary-emphasis focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        >
          {status === "sending" && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
          {tr("Join the waitlist")}
        </button>
        <p className="text-xs text-neutral-muted">{tr("Waitlist only. No payment is taken now.")}</p>
      </fieldset>
    </form>
  );
}
